import React from 'react';
import PropTypes from 'prop-types';

import Modal from 'components/Modal';

import i18n from 'i18n-react';

import IndexSearch from './IndexSearch';

/**
 * Dev notes, How to copy Algolia-Search in other applications.
 *
 * 1) Add the following libraries to the project:
 *    - react-instantsearch-hooks-web [yarn add algoliasearch react-instantsearch-hooks-web]
 *    - @travpro/hooks [yarn add @travpro/hooks]
 * 2) Copy the following files/folders to the project:
 *    - /components/Modal [all files]
 *    - /components/SearchModal [all files]
 *    - /hooks/usePortal.js
 * 3) Add the following aliasses to the webpack config (if not already present):
 *    - hooks: path.resolve(__dirname, 'src', 'hooks')
 * 4) Add search modal to the desired view/component. Since the modal is a controlled component,
 *    keep in mind that the modal state will need to be managed by the parent component.
 *
 * Required adjustments:
 * - For single-client applications, AppSearch is not required.
 *   Remove folder from components/SearchModal and referencese from from SearchModal.js and Modal.js.
 * - In older applications there may be an alternative way of creating <FontAwesomeIcon /> components. Adjust in Modal.js if necessary.
 * - The [useTranslate] hook will not always work in other applications. May be required to replace with alternative translation method (i18n).
 * - The mixin styling and var(...) params in .scss will not always work in other applications.
 *   May be required to replace with alternatives (e.g. css variables or the like). Possible suggestions:
 *   - /components/SearchModal/_searchModal.scss
 *     * var(--primary-color) === $primary-color
 *     * var(--secondary-color) === $secondary-color
 *     * var(--ml-size) === $ml-size
 *     * var(--primary-color-dark) === darken($color: $primary-color, $amount: 10)
 *     * var(--primary-color-light) === lighten($color: $primary-color, $amount: 10)
 *   - /components/Modal/_modal.scss
 * - In applications where scss is added automatically, do not forget to remove the css imports 
 *   (components/SearchModal/Searchmodal.js, components/SearchModal/SearchResult/SearchResult.js and components/Modal/Modal.js)
 * - Remove loadable and LoadableIcon imports from Modal.js if not used and replace with FontAwesomeIcon.
 */

/**
 * A controlled algolia search component, displayed inside of a Modal component.
 *
 * @param {boolean} show - Whether to show the modal or not.
 * @param {Function} onHide - Function to call when the modal is closed.
 * @returns {JSX.Element} SearchModal component with children.
 *
 */
const SearchModal = ({ show, onHide }) => {
  const algolia = {
    id: 'TRCCWLN5MZ',
    searchKey: 'f93ec996b6352f6be2f9f55b4678c013',
  };

  return (
    <div className="search-modal__wrapper">
      <Modal
        id="search-modal__index"
        show={show || false}
        onHide={onHide}
        bgClose
        contentScroll
      >
        <h1 className="">{i18n.translate('searchView.search')}</h1>

        <h3>{i18n.translate('searchView.indexSearchDescription')}</h3>
        <IndexSearch
          algoliaId={algolia.id}
          algoliaSearchKey={algolia.searchKey}
          onHide={onHide}
        />
      </Modal>
    </div>
  );
};

SearchModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default SearchModal;
